"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { type PropsWithChildren, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { twMerge } from "tailwind-merge";

import { env } from "@/env";
import { getFormMaxlength, toErrorString } from "@/globals";
import { type FormSupportType } from "@/schemes/form-support-module.scheme";
import { BUTTON_STYLES } from "@/styles/buttons";
import { INPUT_STYLES, TYPO_STYLES } from "@/styles/typography";
import { WRAPPER_STYLES } from "@/styles/wrapper";
import { ErrorView } from "@/views/error.view";

type FormSupportModuleProps = {
    abos: Record<string, string>;
    entityId: string;
    entityType: string;
    successHref: string | undefined;
};

const testData: Required<FormSupportType> = {
    city: "Musterhausen",
    customerNumber: "11111",
    email: "max@mustermann.com",
    entityId: "",
    entityType: "",
    schoolname: "THG",
    firstname: "Max",
    houseNumber: "5",
    lastname: "Musterman",
    message: "Musternachricht",
    phone: "01514556348",
    product: "digital-abo",
    privacy: true,
    salutation: "male",
    street: "Musterstraße 1",
    subject: "order",
    zip: "58566",
};

const defaultData: Required<FormSupportType> = {
    city: "",
    customerNumber: "",
    email: "",
    entityId: "",
    entityType: "",
    schoolname: "",
    firstname: "",
    houseNumber: "",
    lastname: "",
    message: "",
    phone: "",
    product: "",
    privacy: false,
    salutation: "",
    street: "",
    subject: "",
    zip: "",
};

export const FormSupportModuleView = ({
    successHref,
    entityId,
    entityType,
    abos,
    children,
}: PropsWithChildren<FormSupportModuleProps>) => {
    const EDUPAGE = "EduPage";
    const [error, setError] = useState<string | undefined>();

    const [form, setForm] = useState<FormSupportType>({
        ...defaultData,
        ...(env.NEXT_PUBLIC_USE_TESTDATA ? testData : {}),
        entityId,
        entityType,
    });

    const { executeRecaptcha } = useGoogleReCaptcha();

    const [processing, setProcessing] = useState(false);

    const router = useRouter();

    const handleChange = (values: FormSupportType) => {
        setError(undefined);
        setForm((f) => ({
            ...f,
            ...values,
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!processing) {
            setProcessing(true);

            try {
                if (executeRecaptcha) {
                    const recaptchaToken = await executeRecaptcha(entityId);

                    const response = await fetch("/api/submit", {
                        method: "POST",
                        body: JSON.stringify({ ...form, recaptchaToken }),
                    });

                    if (response.ok && successHref) {
                        router.push(successHref);
                    } else {
                        throw new Error("Das schlug leider fehl.");
                    }
                } else {
                    throw new Error("Sendung aktuell nicht möglich");
                }
            } catch (error) {
                setError(toErrorString(error));
                setProcessing(false);
            }
        }
    };

    if (successHref) {
        return (
            <div className={twMerge("w-full gap-8 grid ")}>
                <form className="flex flex-col gap-8" onSubmit={handleSubmit}>
                    <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                        <span
                            className={twMerge(
                                TYPO_STYLES.bodyExtraBold18(),
                                "col-span-6 text-brandDark",
                            )}
                        >
                            Auf welches unserer Produkte bezieht sich Ihre Kontaktanfrage?
                        </span>
                        <div className="custom-chevron grid col-span-6 sm:col-span-3 sm:col-start-1 sm:col-end-4">
                            <label htmlFor="product" className="gap-2 flex flex-col">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "text-brandDark",
                                    )}
                                >
                                    Produktauswahl
                                </span>
                                <select
                                    className={twMerge(INPUT_STYLES.dropdown())}
                                    required
                                    onChange={(e) =>
                                        handleChange({ product: e.target.value || undefined })
                                    }
                                    defaultValue={form.product}
                                >
                                    <option value="">Auswählen</option>
                                    {Object.entries(abos).map(([key, value]) => (
                                        <option value={key} key={key}>
                                            {value}
                                        </option>
                                    ))}
                                    <option value={EDUPAGE}>{EDUPAGE}</option>
                                </select>
                            </label>
                        </div>
                        {form.product === EDUPAGE && (
                            <div className="grid col-span-6 sm:col-span-3">
                                <div className="flex items-end">
                                    <Link
                                        href="https://edupage-support.raabe.de/"
                                        className={BUTTON_STYLES.secondary()}
                                        prefetch={false}
                                    >
                                        Zum Supportformular für EduPage
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                    {form.product !== EDUPAGE && (
                        <>
                            <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "col-span-6 text-brandDark",
                                    )}
                                >
                                    Bitte hinterlassen Sie uns Ihre Kontaktdaten:
                                </span>
                                <div className="custom-chevron grid col-span-6 sm:col-span-3 sm:col-start-1 sm:col-end-3">
                                    <label htmlFor="salutation" className="gap-2 flex flex-col">
                                        <span
                                            className={twMerge(
                                                TYPO_STYLES.bodyExtraBold18(),
                                                "text-brandDark",
                                            )}
                                        >
                                            Anrede
                                        </span>
                                        <select
                                            className={twMerge(INPUT_STYLES.dropdown())}
                                            onChange={(e) =>
                                                handleChange({
                                                    salutation: e.target.value || undefined,
                                                })
                                            }
                                            defaultValue={form.salutation}
                                        >
                                            <option value="">Auswählen</option>
                                            <option value="Herr">Herr</option>
                                            <option value="Frau">Frau</option>
                                            <option value="Divers">Divers</option>
                                        </select>
                                    </label>
                                </div>
                                <label className="flex flex-col gap-2 col-span-6 sm:col-start-1 col-end-4">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Vorname
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Vorname"
                                        className={INPUT_STYLES.input()}
                                        defaultValue={form.firstname}
                                        onChange={(e) =>
                                            handleChange({ firstname: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("firstname")}
                                    />
                                </label>
                                <label className="flex flex-col gap-2 col-span-6 sm:col-span-3">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Nachname
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Nachname"
                                        className={INPUT_STYLES.input()}
                                        defaultValue={form.lastname}
                                        onChange={(e) =>
                                            handleChange({ lastname: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("lastname")}
                                    />
                                </label>
                            </div>
                            <div className="bg-white px-8 py-10 rounded-3xl grid sm:grid-cols-6 gap-8">
                                <span
                                    className={twMerge(
                                        TYPO_STYLES.bodyExtraBold18(),
                                        "col-span-6 text-brandDark",
                                    )}
                                >
                                    Adresse (Schul- oder Privatadresse)
                                </span>
                                <label className="flex flex-col gap-2 col-span-6 sm:col-span-4">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Schulname (nur bei Schulbezug angeben)
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Schulname"
                                        className={INPUT_STYLES.input()}
                                        defaultValue={form.schoolname}
                                        onChange={(e) =>
                                            handleChange({
                                                schoolname: e.target.value || undefined,
                                            })
                                        }
                                        maxLength={getFormMaxlength("schoolname")}
                                    />
                                </label>
                                <label className="flex flex-col gap-2 col-span-6 sm:col-span-4">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Straße
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Straße"
                                        className={INPUT_STYLES.input()}
                                        defaultValue={form.street}
                                        onChange={(e) =>
                                            handleChange({ street: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("street")}
                                        pattern=".*\S.*"
                                        title="Bitte geben Sie einen gülten Wert ein."
                                    />
                                </label>
                                <label className="flex flex-col gap-2 col-span-6 sm:col-span-2">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Hausnummer
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Hausnummer"
                                        className={INPUT_STYLES.input()}
                                        defaultValue={form.houseNumber}
                                        onChange={(e) =>
                                            handleChange({
                                                houseNumber: e.target.value || undefined,
                                            })
                                        }
                                        maxLength={getFormMaxlength("houseNumber")}
                                        pattern=".*\S.*"
                                        title="Bitte geben Sie einen gülten Wert ein."
                                    />
                                </label>
                                <label className="flex flex-col gap-2 col-span-6 sm:col-span-4">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Ort
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Ort"
                                        className={INPUT_STYLES.input()}
                                        defaultValue={form.city}
                                        onChange={(e) =>
                                            handleChange({ city: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("city")}
                                        pattern=".*\S.*"
                                        title="Bitte geben Sie einen gülten Wert ein."
                                    />
                                </label>
                                <label className="flex flex-col gap-2 col-span-6 sm:col-span-2 relative">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        PLZ
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="PLZ"
                                        className={INPUT_STYLES.input()}
                                        defaultValue={form.zip}
                                        onChange={(e) =>
                                            handleChange({ zip: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("zip")}
                                        pattern=".*\S.*"
                                        title="Bitte geben Sie einen gülten Wert ein."
                                    />
                                </label>
                            </div>
                            <div className="bg-white px-8 py-10 rounded-3xl grid gap-8">
                                <label className="grid col-span-6 gap-2">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        E-Mail: Bei Anfragen für Ihre Schule geben Sie bitte Ihre
                                        dienstliche E-Mail Adresse an.
                                    </span>
                                    <input
                                        required
                                        type="email"
                                        placeholder="E-Mail"
                                        defaultValue={form.email}
                                        className={INPUT_STYLES.input()}
                                        onChange={(e) =>
                                            handleChange({ email: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("email")}
                                    />
                                </label>
                                <div className="custom-chevron grid col-span-6 sm:col-span-2 gap-2">
                                    <label htmlFor="subject" className="flex flex-col gap-2">
                                        <span
                                            className={twMerge(
                                                TYPO_STYLES.bodyExtraBold18(),
                                                "text-brandDark",
                                            )}
                                        >
                                            Betreff
                                        </span>
                                        <select
                                            required
                                            className={INPUT_STYLES.dropdown()}
                                            defaultValue={form.subject}
                                            onChange={(e) =>
                                                handleChange({
                                                    subject: e.target.value || undefined,
                                                })
                                            }
                                        >
                                            <option value="">Auswählen</option>
                                            <option value="Abonnement/Download">
                                                Abonnement/Download
                                            </option>
                                            <option value="Adressänderung">Adressänderung</option>
                                            <option value="Angebot/Anfrage">Angebot/Anfrage</option>
                                            <option value="Bestellung/Lieferung">
                                                Bestellung/Lieferung
                                            </option>
                                            <option value="Login Probleme">Login Probleme</option>
                                            <option value="Rechnung/Mahnung">
                                                Rechnung/Mahnung
                                            </option>
                                            <option value="Reklamation">Reklamation</option>
                                            <option value="Sonstiges">Sonstiges</option>
                                        </select>
                                    </label>
                                </div>
                                <label className="grid gap-2 col-span-6 sm:col-span-2">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Telefonnummer
                                    </span>
                                    <input
                                        type="tel"
                                        placeholder="Telefonnummer"
                                        defaultValue={form.phone}
                                        className={twMerge(
                                            INPUT_STYLES.input(),
                                            "invalid:border-red-500",
                                        )}
                                        onChange={(e) =>
                                            handleChange({ phone: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("phone")}
                                        pattern="^\+?[0-9\s\-()]+$"
                                        title="Bitte geben Sie einen gülten Wert ein."
                                    />
                                </label>
                                <label className="grid gap-2 col-span-6 sm:col-span-2">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Kundennummer
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Kundennummer"
                                        defaultValue={form.customerNumber}
                                        className={twMerge(
                                            INPUT_STYLES.input(),
                                            "invalid:border-red-500",
                                        )}
                                        onChange={(e) =>
                                            handleChange({
                                                customerNumber: e.target.value || undefined,
                                            })
                                        }
                                        maxLength={getFormMaxlength("customerNumber")}
                                        pattern=".*\S.*"
                                        title="Bitte geben Sie einen gülten Wert ein."
                                    />
                                </label>
                            </div>
                            <div className="bg-white px-8 py-10 rounded-3xl">
                                <label htmlFor="message" className="flex flex-col gap-2">
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyExtraBold18(),
                                            "text-brandDark",
                                        )}
                                    >
                                        Ihre Nachricht an uns:
                                    </span>
                                    <textarea
                                        required
                                        defaultValue={form.message}
                                        rows={5}
                                        className={INPUT_STYLES.textarea()}
                                        onChange={(e) =>
                                            handleChange({ message: e.target.value || undefined })
                                        }
                                        maxLength={getFormMaxlength("message")}
                                    ></textarea>
                                    <span
                                        className={twMerge(
                                            TYPO_STYLES.bodyNormal14(),
                                            "text-brandDark",
                                        )}
                                    >
                                        (max. {getFormMaxlength("message")} Zeichen)
                                    </span>
                                </label>
                            </div>
                            {children && (
                                <div className="checkbox-formular relative">
                                    <label>
                                        <input
                                            defaultChecked={form.privacy}
                                            pattern=".*\S.*"
                                            required
                                            type="checkbox"
                                            onChange={(e) =>
                                                handleChange({ privacy: e.target.checked })
                                            }
                                        />
                                        {children}
                                        <span></span>
                                    </label>
                                </div>
                            )}
                            <div className={twMerge(WRAPPER_STYLES.HCenterRight(), "gap-6")}>
                                {error && (
                                    <p
                                        className={twMerge(
                                            TYPO_STYLES.bodyNormal18(),
                                            "text-redError",
                                        )}
                                    >
                                        {error}
                                    </p>
                                )}
                                <input
                                    type="submit"
                                    className={twMerge(BUTTON_STYLES.primary(), "cursor-pointer")}
                                    value={processing ? "...wird gesendet" : "Anfrage absenden"}
                                />
                            </div>
                        </>
                    )}
                </form>
            </div>
        );
    } else {
        return <ErrorView>Success-Page is not provided.</ErrorView>;
    }
};
